import { AnalyticsMetrics, DashboardFilter, TimeSeriesData, AlertRule } from '../types';
import { subHours, subDays, format, parseISO } from 'date-fns';

export class AnalyticsService {
  private readonly baseUrl: string;

  constructor(baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787') {
    this.baseUrl = baseUrl;
  }

  /**
   * Get analytics metrics with filters
   */
  async getMetrics(filter: DashboardFilter): Promise<AnalyticsMetrics> {
    const params = new URLSearchParams({
      timeRange: filter.timeRange,
      refreshInterval: filter.refreshInterval.toString(),
    });

    if (filter.apps?.length) {
      params.set('apps', filter.apps.join(','));
    }

    if (filter.status?.length) {
      params.set('status', filter.status.join(','));
    }

    const response = await fetch(`${this.baseUrl}/api/analytics/metrics?${params}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch analytics: ${response.statusText}`);
    }

    const data = await response.json();
    return data.metrics || this.generateMockMetrics(filter);
  }

  /**
   * Get real-time metrics (used for live updates)
   */
  async getRealTimeMetrics(): Promise<Partial<AnalyticsMetrics>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/analytics/realtime`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch real-time metrics: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.warn('Real-time metrics unavailable, using mock data');
      return this.generateMockRealTimeUpdate();
    }
  }

  /**
   * Get system health status
   */
  async getSystemHealth(): Promise<{
    overall: 'healthy' | 'degraded' | 'down';
    services: Array<{
      name: string;
      status: 'healthy' | 'degraded' | 'down';
      responseTime?: number;
      lastCheck: string;
    }>;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/analytics/health`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch system health: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      return this.generateMockHealthStatus();
    }
  }

  /**
   * Get alert rules
   */
  async getAlertRules(): Promise<AlertRule[]> {
    try {
      const response = await fetch(`${this.baseUrl}/api/analytics/alerts`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch alert rules: ${response.statusText}`);
      }

      const data = await response.json();
      return data.rules || [];
    } catch (error) {
      return this.generateMockAlertRules();
    }
  }

  /**
   * Create or update alert rule
   */
  async saveAlertRule(rule: Omit<AlertRule, 'id' | 'createdAt'>): Promise<AlertRule> {
    const response = await fetch(`${this.baseUrl}/api/analytics/alerts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(rule),
    });

    if (!response.ok) {
      throw new Error(`Failed to save alert rule: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete alert rule
   */
  async deleteAlertRule(ruleId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/analytics/alerts/${ruleId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete alert rule: ${response.statusText}`);
    }
  }

  /**
   * Export analytics data
   */
  async exportData(filter: DashboardFilter, format: 'csv' | 'json'): Promise<Blob> {
    const params = new URLSearchParams({
      timeRange: filter.timeRange,
      format,
    });

    if (filter.apps?.length) {
      params.set('apps', filter.apps.join(','));
    }

    const response = await fetch(`${this.baseUrl}/api/analytics/export?${params}`);
    
    if (!response.ok) {
      throw new Error(`Failed to export data: ${response.statusText}`);
    }

    return response.blob();
  }

  // Mock data generators for development/demo purposes
  private generateMockMetrics(filter: DashboardFilter): AnalyticsMetrics {
    const now = new Date();
    const timePoints = this.getTimePoints(filter.timeRange, now);

    return {
      overview: {
        totalConnections: Math.floor(Math.random() * 100) + 50,
        activeConnections: Math.floor(Math.random() * 80) + 40,
        totalRequests: Math.floor(Math.random() * 10000) + 5000,
        averageResponseTime: Math.floor(Math.random() * 200) + 100,
        errorRate: Math.random() * 5,
        uptime: 99.8 + Math.random() * 0.2,
      },
      timeSeries: {
        requests: timePoints.map(timestamp => ({
          timestamp,
          value: Math.floor(Math.random() * 100) + 50,
        })),
        responseTime: timePoints.map(timestamp => ({
          timestamp,
          value: Math.floor(Math.random() * 300) + 100,
        })),
        errors: timePoints.map(timestamp => ({
          timestamp,
          value: Math.floor(Math.random() * 10),
        })),
        connections: timePoints.map(timestamp => ({
          timestamp,
          value: Math.floor(Math.random() * 20) + 40,
        })),
      },
      topApps: [
        {
          appId: 'gmail',
          appName: 'Gmail',
          requests: Math.floor(Math.random() * 1000) + 500,
          errors: Math.floor(Math.random() * 10),
          avgResponseTime: Math.floor(Math.random() * 200) + 100,
          status: 'healthy',
        },
        {
          appId: 'slack',
          appName: 'Slack',
          requests: Math.floor(Math.random() * 800) + 300,
          errors: Math.floor(Math.random() * 5),
          avgResponseTime: Math.floor(Math.random() * 150) + 80,
          status: 'healthy',
        },
        {
          appId: 'github',
          appName: 'GitHub',
          requests: Math.floor(Math.random() * 600) + 200,
          errors: Math.floor(Math.random() * 8),
          avgResponseTime: Math.floor(Math.random() * 300) + 150,
          status: 'degraded',
        },
        {
          appId: 'google-sheets',
          appName: 'Google Sheets',
          requests: Math.floor(Math.random() * 400) + 100,
          errors: Math.floor(Math.random() * 3),
          avgResponseTime: Math.floor(Math.random() * 250) + 120,
          status: 'healthy',
        },
      ],
      recentActivity: this.generateMockActivity(),
    };
  }

  private generateMockRealTimeUpdate(): Partial<AnalyticsMetrics> {
    return {
      overview: {
        totalConnections: Math.floor(Math.random() * 100) + 50,
        activeConnections: Math.floor(Math.random() * 80) + 40,
        totalRequests: Math.floor(Math.random() * 10000) + 5000,
        averageResponseTime: Math.floor(Math.random() * 200) + 100,
        errorRate: Math.random() * 5,
        uptime: 99.8 + Math.random() * 0.2,
      },
    };
  }

  private generateMockHealthStatus() {
    return {
      overall: 'healthy' as const,
      services: [
        {
          name: 'API Gateway',
          status: 'healthy' as const,
          responseTime: 45,
          lastCheck: new Date().toISOString(),
        },
        {
          name: 'Database',
          status: 'healthy' as const,
          responseTime: 12,
          lastCheck: new Date().toISOString(),
        },
        {
          name: 'OAuth Service',
          status: 'degraded' as const,
          responseTime: 180,
          lastCheck: new Date().toISOString(),
        },
        {
          name: 'Pipedream Integration',
          status: 'healthy' as const,
          responseTime: 95,
          lastCheck: new Date().toISOString(),
        },
      ],
    };
  }

  private generateMockAlertRules(): AlertRule[] {
    return [
      {
        id: 'alert_1',
        name: 'High Response Time',
        condition: {
          metric: 'responseTime',
          operator: 'gt',
          threshold: 500,
          duration: 5,
        },
        actions: [
          {
            type: 'email',
            target: 'admin@example.com',
          },
        ],
        enabled: true,
        createdAt: new Date().toISOString(),
      },
      {
        id: 'alert_2',
        name: 'High Error Rate',
        condition: {
          metric: 'errorRate',
          operator: 'gt',
          threshold: 10,
          duration: 3,
        },
        actions: [
          {
            type: 'slack',
            target: '#alerts',
          },
        ],
        enabled: true,
        createdAt: new Date().toISOString(),
      },
    ];
  }

  private generateMockActivity() {
    const activities = [
      'New Gmail connection established',
      'Configuration updated for Slack workspace',
      'GitHub webhook failed - retrying',
      'OAuth token refreshed for Google Sheets',
      'Alert triggered: High response time detected',
      'System backup completed successfully',
      'New user registered',
      'API rate limit warning for Notion',
    ];

    return activities.slice(0, 5).map((message, index) => ({
      id: `activity_${index}`,
      type: ['connection', 'config', 'error', 'connection', 'error', 'info', 'connection', 'warning'][index] as any,
      message,
      timestamp: new Date(Date.now() - index * 300000).toISOString(),
      severity: ['info', 'info', 'error', 'info', 'warning', 'info', 'info', 'warning'][index] as any,
      appName: ['Gmail', 'Slack', 'GitHub', 'Google Sheets', undefined, undefined, undefined, 'Notion'][index],
    }));
  }

  private getTimePoints(timeRange: DashboardFilter['timeRange'], endTime: Date): string[] {
    const points: Date[] = [];
    let interval: number;
    let count: number;

    switch (timeRange) {
      case '1h':
        interval = 5 * 60 * 1000; // 5 minutes
        count = 12;
        break;
      case '24h':
        interval = 60 * 60 * 1000; // 1 hour
        count = 24;
        break;
      case '7d':
        interval = 6 * 60 * 60 * 1000; // 6 hours
        count = 28;
        break;
      case '30d':
        interval = 24 * 60 * 60 * 1000; // 1 day
        count = 30;
        break;
      case '90d':
        interval = 3 * 24 * 60 * 60 * 1000; // 3 days
        count = 30;
        break;
    }

    for (let i = count - 1; i >= 0; i--) {
      points.push(new Date(endTime.getTime() - i * interval));
    }

    return points.map(date => date.toISOString());
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();